import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Divider,
  Button,
  Badge,
  Flex,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  ModalOverlay,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Icon,
  Switch,
  FormControl,
  FormLabel,
  Select,
} from '@chakra-ui/react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// Assumi di aver creato una componente per i dettagli dell'ordine
import { FaCreditCard, FaMoneyBillWave, FaUniversity } from 'react-icons/fa';
import DettaglioOrdine from './DettaglioOrdine';
import EventPaymentSummaryPanel from './EventPaymentSummaryPanel';
import SiblingDiscountBadge, {
  orderHasSiblingDiscount,
} from './SiblingDiscountBadge';
import { orderHasAdminOverrideBug } from '../../utils/orderAdminOverride';
import PaymentForm from '../PaymentForm';
import { formatDateAbbr } from '../../utils';
import { filterRewardOrders } from '../../utils/rewardOrderUtils';
import { isOrderAlreadyCreated } from '../../utils/orderCreation';
import {
  getMerchFulfillmentLabel,
  getMerchFulfillmentColor,
} from '../../utils/merchFulfillmentLabels';

const PaymentModal = ({ isOpen, onClose, title, icon, content }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader display="flex" alignItems="center">
          <Icon as={icon} mr={2} />
          {title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="start" spacing={4}>
            <Box dangerouslySetInnerHTML={{ __html: content }} />
            <Button colorScheme="blue" onClick={onClose} w="full">
              Chiudi
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const PaymentMethods = () => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openModal = (method) => {
    setSelectedPayment(method);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedPayment(null);
  };

  const paymentOptions = [
    {
      name: 'Pagamento Elettronico',
      icon: FaCreditCard,
      content: `
    <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
      Come funziona
    </h2>
    <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
      Per effettuare il pagamento, è sufficiente registrarsi e selezionare l'opzione <strong>"Pagamento Elettronico"</strong> 
      durante la prenotazione. Il sistema guiderà automaticamente alla procedura di pagamento online.
    </p>

    <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
      Conferma dell'iscrizione
    </h2>
    <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
      L'iscrizione sarà confermata solo dopo che il pagamento elettronico sarà completato correttamente. 
      Riceverai un'email di conferma con i dettagli della transazione.
    </p>

    <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
      Metodo di pagamento
    </h2>
    <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
      Il pagamento avverrà in modo sicuro tramite il nostro sistema elettronico, senza necessità di 
      inserire manualmente email o causali di pagamento.
    </p>

    <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
      Ordini in sospeso
    </h2>
    <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
      È possibile utilizzare il pagamento elettronico anche per completare eventuali <strong>ordini in sospeso</strong>. 
      Basta accedere alla propria area riservata, visualizzare l’elenco delle prenotazioni e selezionare l’ordine da saldare.
    </p>

    <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
      Cosa fare dopo il pagamento
    </h2>
    <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
      Una volta completato il pagamento, puoi controllare lo stato della tua prenotazione nella tua area riservata. 
      Se hai bisogno di assistenza, contattaci!
    </p>
  `,
    },

    {
      name: 'Pagamento in Loco',
      icon: FaMoneyBillWave,
      content: `
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:10px; border-bottom:2px solid #E2E8F0; padding-bottom:5px;">
          Come funziona
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.7; margin-bottom:12px;">
          Il pagamento può essere effettuato direttamente il giorno dell'evento presso la reception. 
          Sono accettati pagamenti in contanti, Satispay e con carte di credito/debito. 
          Ti basterà comunicare il tuo nome agli organizzatori al momento del pagamento.
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:10px; border-bottom:2px solid #E2E8F0; padding-bottom:5px;">
          Dove pagare
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.7; margin-bottom:12px;">
          Il pagamento deve essere effettuato all'ingresso dell'evento, prima di accedere all'attività. 
          Gli organizzatori saranno disponibili per fornire eventuali indicazioni.
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:10px; border-bottom:2px solid #E2E8F0; padding-bottom:5px;">
          Cosa portare
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.7; margin-bottom:8px;">
          Per agevolare le operazioni, consigliamo di portare:
        </p>
        <ul style="font-size:16px; color:#4A5568; line-height:1.7; padding-left:18px; margin-bottom:12px;">
          <li>Importo esatto per il pagamento in contanti, se possibile.</li>
          <li>Conferma della prenotazione (digitale o cartacea).</li>
          <li>Documento di identità, se richiesto dagli organizzatori.</li>
        </ul>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:10px; border-bottom:2px solid #E2E8F0; padding-bottom:5px;">
          Informazioni e assistenza
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.7;">
          Per qualsiasi domanda o necessità di assistenza, ti invitiamo a contattare l'organizzazione prima dell'evento.
        </p>
      `,
    },

    {
      name: 'Bonifico Bancario',
      icon: FaUniversity,
      content: `
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
          Come funziona
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
          Il pagamento deve essere effettuato tramite bonifico bancario prima dell'inizio dell'attività. 
          L'iscrizione sarà confermata solo dopo la ricezione dell'importo dovuto.
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
          Dati per il bonifico
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
          <strong>Intestatario:</strong> CIL - CREATIVITA IN LIBERTA ASSOCIAZIONE DI PROMOZIONE SOC<br>
          <strong>IBAN:</strong> IT10K0200830362000107087594<br>
          <strong>Importo:</strong> Indicato al momento della prenotazione
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
          Causale del pagamento
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.8; margin-bottom:20px;">
          È obbligatorio inserire l'<strong>ID Ordine</strong> nella causale del bonifico per consentire l'abbinamento del pagamento alla prenotazione. 
          Trovi il numero d'ordine nei dettagli del tuo ordine.
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
          Esempio di bonifico
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.8; background:#F7FAFC; padding:12px; border-radius:6px; border-left:4px solid #4299E1; margin-bottom:20px;">
          <strong>Beneficiario:</strong> CIL - CREATIVITA IN LIBERTA ASSOCIAZIONE DI PROMOZIONE SOC<br>
          <strong>IBAN:</strong> IT10K0200830362000107087594<br>
          <strong>Importo:</strong> 50,00€<br>
          <strong>Causale:</strong> dchsxgy7akucrdcj6kvlnyeq
        </p>
    
        <h2 style="color:#2D3748; font-size:20px; font-weight:600; margin-bottom:15px; border-bottom:2px solid #E2E8F0; padding-bottom:8px;">
          Conferma del pagamento
        </h2>
        <p style="font-size:16px; color:#4A5568; line-height:1.8;">
          Dopo aver effettuato il bonifico, invia la ricevuta di pagamento via email agli organizzatori per velocizzare la conferma della tua iscrizione.
        </p>
      `,
    },
  ];

  return (
    <Flex
      width="100%"
      justifyContent="center"
      flexDirection={{ base: 'column', md: 'row' }}
    >
      {paymentOptions.map((method) => (
        <Button key={method.name} onClick={() => openModal(method)} m={2}>
          {method.name}
        </Button>
      ))}

      {selectedPayment && (
        <PaymentModal
          isOpen={isOpen}
          onClose={closeModal}
          title={selectedPayment.name}
          icon={selectedPayment.icon}
          content={selectedPayment.content}
        />
      )}
    </Flex>
  );
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const Orders = ({ userData, requestUserData }) => {
  const location = useLocation();
  const { orders } = userData;
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedProductDataToPay, setSelectedProductToPay] = useState(null);
  const [selectedOrderToPay, setSelectedOrderToPay] = useState(null);

  const [clientPaymentIntentId, setClientPaymentIntentId] = useState('');
  const [showFreeOrders, setShowFreeOrders] = useState(true);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const orderRefs = useRef({});
  const hasAutoFocusedOrder = useRef(false);
  const targetOrderId = new URLSearchParams(location.search).get('focusOrder');

  // Filtra gli ordini escludendo tesseramenti e ordini catalogo premi (pagina dedicata)
  const filteredOrders = React.useMemo(() => {
    const nonTesseramento = (orders || []).filter(
      (order) => !order.tesseramento
    );
    const rewardKeys = new Set(
      filterRewardOrders(nonTesseramento).map((o) =>
        String(o.documentId || o.id || '')
      )
    );
    return nonTesseramento.filter(
      (order) => !rewardKeys.has(String(order.documentId || order.id || ''))
    );
  }, [orders]);

  // Anni presenti negli ordini (dal più recente)
  const availableYears = React.useMemo(() => {
    const years = new Set(
      (filteredOrders || [])
        .map((o) => o.createdAt && new Date(o.createdAt).getFullYear())
        .filter(Boolean)
    );
    return ['all', ...Array.from(years).sort((a, b) => Number(b) - Number(a))];
  }, [filteredOrders]);

  // Ordini visibili: filtrati per anno e (opzionalmente) per ordini gratuiti
  const displayedOrders = React.useMemo(() => {
    let list = filteredOrders.filter((order) => {
      const year = order.createdAt
        ? new Date(order.createdAt).getFullYear()
        : null;
      return (
        selectedYear === 'all' ||
        (year !== null && year === Number(selectedYear))
      );
    });
    list = list.filter(
      (order) => showFreeOrders || !(order.event?.is_free || order.price === 0)
    );
    return list;
  }, [filteredOrders, selectedYear, showFreeOrders]);

  const selectedParticipantId =
    displayedOrders[selectedIndex]?.socio?.documentId;

  // Riallinea selectedYear se non è più tra gli anni disponibili (es. dopo caricamento dati)
  useEffect(() => {
    if (
      selectedYear !== 'all' &&
      availableYears.length > 1 &&
      !availableYears.includes(selectedYear)
    ) {
      setSelectedYear(availableYears[1] || 'all'); // availableYears[0] è 'all'
    }
  }, [availableYears, selectedYear]);

  const onModalClose = () => {
    setPaymentError('');
    setClientSecret('');
    setClientPaymentIntentId('');
    setPaymentSuccess(false);
    setSelectedIndex(null);
    setSelectedProductToPay(null);
    setSelectedOrderToPay(null);
  };

  const createOrder = async ({
    paymentIntentId,
    paymentMethodId,
    paymentStatus = 'pending',
    paymentMethod = 'contanti',
  } = {}) => {
    const eventId =
      selectedProductDataToPay?.eventId ||
      displayedOrders[selectedIndex]?.event?.documentId;
    const participantId =
      selectedProductDataToPay?.participantId ||
      selectedProductDataToPay?.socioId ||
      selectedParticipantId;

    if (!eventId || !participantId) {
      setPaymentError(
        'Dati ordine mancanti. Chiudi e riprova dalla scheda ordine.'
      );
      return;
    }

    try {
      const response = await axios.post('/api/ordini', {
        eventId,
        participantId,
        paymentIntentId,
        paymentStatus,
        paymentMethod,
        paymentMethodId,
        paymentIntents: clientPaymentIntentId,
        eventVariants: {
          [selectedProductDataToPay.id]: {
            type: 'variant',
            priceOption: 'saldo',
            name: selectedProductDataToPay.name,
            amount: selectedProductDataToPay.amount,
            applyDiscount: selectedProductDataToPay.applyDiscount,
          },
        },
        amount: selectedProductDataToPay.amount,
      });

      if (
        response.status === 200 ||
        isOrderAlreadyCreated(response.data) ||
        response.data?.order
      ) {
        setPaymentSuccess(true);
        requestUserData({ withLoading: false });

        setTimeout(() => {
          setPaymentSuccess(false);
          setSelectedProductToPay('');
          setSelectedOrderToPay(null);
        }, 2000);
      } else {
        setPaymentError(`Errore con la creazione dell'ordine`);
      }
    } catch (error) {
      if (isOrderAlreadyCreated(error.response?.data)) {
        setPaymentSuccess(true);
        requestUserData({ withLoading: false });
        setTimeout(() => {
          setPaymentSuccess(false);
          setSelectedProductToPay('');
          setSelectedOrderToPay(null);
        }, 2000);
        return;
      }
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      setPaymentError(message || `Errore con la creazione dell'ordine`);
    }
  };

  const updateOrder = async ({
    paymentIntentId,
    paymentMethodId,
    paymentStatus = 'pending',
    paymentMethod = 'contanti',
    orderId,
  } = {}) => {
    const response = await axios.put(`/api/ordini/`, {
      orderId,
      paymentIntentId,
      paymentMethodId,
      paymentStatus,
      paymentMethod,
      paymentIntents: clientPaymentIntentId,
    });

    if (response.status === 200) {
      setPaymentSuccess(true);
      requestUserData({ withLoading: false });

      setTimeout(() => {
        setPaymentSuccess(false);
        setSelectedProductToPay('');
        setSelectedOrderToPay(null);
      }, 2000);
    } else {
      setPaymentError(`Errore con la creazione dell'ordine`);
    }
  };

  // Funzione per determinare il colore del badge in base allo stato del pagamento
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'green';
      case 'processing':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      case 'refunded':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'succeeded':
        return 'Pagato';
      case 'processing':
        return 'In lavorazione';
      case 'failed':
        return 'Fallito';
      case 'pending':
        return 'In attesa';
      case 'refunded':
        return 'Rimborsato';
      default:
        return 'Sconosciuto';
    }
  };

  // Gestione del click su una card: seleziona l'indice dell'ordine
  const handleCardClick = (index) => {
    if (selectedIndex === index) {
      setSelectedIndex('');
    } else {
      setSelectedIndex(index);
    }
  };

  // Callback per chiudere i dettagli (ad esempio, dopo un pagamento avvenuto con successo)
  const onPaymentSuccess = () => {
    setSelectedIndex(null);
  };

  const createPaymentIntent = async ({
    socioId,
    amount,
    eventId,
    eventUrlTitle,
  }) => {
    setLoadingSecret(true);

    try {
      const response = await axios.post('/api/create-payment-intent', {
        currency: 'eur',
        eventUrlTitle,
        eventId,
        socioId,
        stripeCustomerId: userData?.stripeCustomerId,
        amount: amount * 100,
        eventVariants: {
          [selectedProductDataToPay.id]: {
            type: 'variant',
            priceOption: 'saldo',
            name: selectedProductDataToPay.name,
            amount: selectedProductDataToPay.amount,
            applyDiscount: selectedProductDataToPay.applyDiscount,
          },
        },
      });

      setClientSecret(response.data.clientSecret);
      setClientPaymentIntentId(response.data.localPaymentIntentId);
      setPaymentError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating PaymentIntent:', error);
      setPaymentError(error.message);
    } finally {
      setLoadingSecret(false);
    }
  };

  const createOrderPaymentIntent = async ({
    socioId,
    eventId,
    eventUrlTitle,
  }) => {
    setLoadingSecret(true);

    try {
      // Calcola l'importo totale basandosi sui prezzi effettivi delle varianti
      const totalAmount = selectedOrderToPay.metadata.products.reduce(
        (sum, product) => {
          // Per le varianti, usa prezzoVariante se disponibile, altrimenti amount
          const productPrice =
            product.type === 'variant' && product.prezzoVariante !== undefined
              ? product.prezzoVariante
              : product.amount;
          return sum + productPrice;
        },
        0
      );

      const response = await axios.post('/api/create-payment-intent', {
        currency: 'eur',
        eventUrlTitle,
        eventId,
        socioId,
        order: selectedOrderToPay.documentId,
        stripeCustomerId: userData?.stripeCustomerId,
        amount: totalAmount * 100,
        eventVariants: selectedOrderToPay.metadata.products.reduce(
          (acc, product) => {
            return {
              ...acc,
              [product.id]: {
                type: product.type,
                priceOption: product.paymentType,
                name: product.name,
                amount:
                  product.type === 'variant' &&
                  product.prezzoVariante !== undefined
                    ? product.prezzoVariante
                    : product.amount,
                applyDiscount: product.applyDiscount,
              },
            };
          },
          {}
        ),
      });

      setClientSecret(response.data.clientSecret);
      setClientPaymentIntentId(response.data.localPaymentIntentId);
      setPaymentError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating PaymentIntent:', error);
      setPaymentError(error.message);
    } finally {
      setLoadingSecret(false);
    }
  };

  // Callback per gestire il click del "Paga Saldo"
  const onHandleSaldo = ({
    productId,
    amount,
    socioId,
    applyDiscount,
    initialEventPrice,
  }) => {
    const selectedProductToPay = filteredOrders.reduce((acc, order) => {
      if (socioId && order.socio?.documentId !== socioId) {
        return acc;
      }
      const productToPay = order.metadata?.products?.find(
        (product) =>
          product.id === productId && product.paymentType === 'acconto'
      );

      if (productToPay) {
        return productToPay;
      }

      return acc;
    }, null);

    if (selectedProductToPay) {
      const order = filteredOrders.find(({ metadata, socio }) => {
        if (socioId && socio?.documentId !== socioId) {
          return false;
        }
        const productToPay = metadata?.products?.find(
          (product) =>
            product.id === productId && product.paymentType === 'acconto'
        );

        return Boolean(productToPay);
      }, null);

      const { Sconto_Fratelli: scontoFratelli } = order?.event || {};

      let amountToPay = initialEventPrice - amount;

      if (applyDiscount) {
        const prezzoEventoScontato = scontoFratelli
          ? initialEventPrice - (parseFloat(scontoFratelli) || 0)
          : 0;

        amountToPay = prezzoEventoScontato - amount;
      }

      setSelectedProductToPay({
        id: selectedProductToPay.id,
        amount: amountToPay,
        name: selectedProductToPay.name,
        applyDiscount,
        socioId,
        participantId: order?.socio?.documentId,
        eventId: order?.event?.documentId,
        eventUrlTitle: order?.event?.urlTitle,
      });

      const orderIndex = displayedOrders.findIndex(
        (item) =>
          String(item.documentId || item.id) ===
          String(order?.documentId || order?.id)
      );
      if (orderIndex >= 0) {
        setSelectedIndex(orderIndex);
      }
    }
  };

  const onHandleMainPagaOra = (order) => {
    // da creare una nuova route per aggiornare l'ordine
    setSelectedOrderToPay(order);
  };

  const handleFocusOrder = (orderId) => {
    if (!orderId) return;
    setSelectedYear('all');
    const orderIndex = displayedOrders.findIndex(
      (order) => String(order.documentId || order.id) === String(orderId)
    );
    const fallbackIndex = filteredOrders.findIndex(
      (order) => String(order.documentId || order.id) === String(orderId)
    );
    const index = orderIndex >= 0 ? orderIndex : fallbackIndex;
    if (index >= 0) {
      setSelectedIndex(index);
      setTimeout(() => {
        const ref = orderRefs.current[orderId];
        ref?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const eventPaymentSummaries = userData?.eventPaymentSummaries || [];

  useEffect(() => {
    if (selectedProductDataToPay) {
      const selectedProductToPay = filteredOrders.reduce((acc, order) => {
        const productToPay = order.metadata.products.find(
          (product) =>
            product.id === selectedProductDataToPay.id &&
            product.paymentType === 'acconto' &&
            selectedProductDataToPay.socioId === order.socio.documentId
        );

        if (productToPay) {
          return productToPay;
        }

        return acc;
      }, null);

      if (selectedProductToPay) {
        const order = filteredOrders.find(({ metadata, socio }) => {
          const productToPay = metadata.products.find(
            (product) =>
              product.id === selectedProductDataToPay.id &&
              product.paymentType === 'acconto' &&
              selectedProductDataToPay.socioId === socio.documentId
          );

          return productToPay;
        }, null);

        createPaymentIntent({
          socioId:
            selectedProductDataToPay.participantId || order?.socio?.documentId,
          amount: selectedProductDataToPay.amount,
          eventId: selectedProductDataToPay.eventId || order?.event?.documentId,
          eventUrlTitle:
            selectedProductDataToPay.eventUrlTitle || order?.event?.urlTitle,
        });
      }
    }
  }, [selectedProductDataToPay]);

  useEffect(() => {
    if (selectedOrderToPay) {
      createOrderPaymentIntent({
        socioId: selectedOrderToPay?.socio?.documentId,
        eventId: selectedOrderToPay?.event?.documentId,
        eventUrlTitle: selectedOrderToPay?.event?.urlTitle,
      });
    }
  }, [selectedOrderToPay]);

  useEffect(() => {
    if (!targetOrderId) return;

    setSelectedYear('all');
    setShowFreeOrders(true);
  }, [targetOrderId]);

  useEffect(() => {
    if (!targetOrderId || hasAutoFocusedOrder.current) return;

    const targetIndex = displayedOrders.findIndex((order) => {
      const currentOrderId = String(order.documentId || order.id || '');
      return currentOrderId === targetOrderId;
    });

    if (targetIndex === -1) return;

    setSelectedIndex(targetIndex);
    hasAutoFocusedOrder.current = true;

    setTimeout(() => {
      const element = orderRefs.current[targetOrderId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
  }, [targetOrderId, displayedOrders]);

  return (
    <>
      <Box
        as="span"
        bg="#2f3136"
        p={{ base: 6, md: 8 }}
        display="block"
        borderRadius="20px"
        boxShadow="0 20px 40px rgba(0,0,0,0.3)"
        border="1px solid"
        borderColor="gray.600"
        minH={{ base: 'unset', md: 'unset' }}
      >
        {filteredOrders.length > 0 ? (
          <>
            <Flex
              justify="space-between"
              align="center"
              mb={4}
              flexWrap="wrap"
              gap={3}
            >
              <Heading
                size="lg"
                color="white"
                id="ordini-title"
                fontWeight="800"
              >
                I Tuoi Ordini
              </Heading>
              <HStack spacing={4} flexWrap="wrap">
                {/* Filtro anno */}
                <FormControl width="auto" minW="120px">
                  <FormLabel
                    htmlFor="filter-year"
                    mb="0"
                    fontSize="sm"
                    color="gray.300"
                  >
                    Anno
                  </FormLabel>
                  <Select
                    id="filter-year"
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(e.target.value);
                      setSelectedIndex(null);
                    }}
                    size="md"
                    bg="gray.700"
                    color="white"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'gray.500' }}
                  >
                    <option value="all">Tutti</option>
                    {availableYears
                      .filter((y) => y !== 'all')
                      .map((y) => (
                        <option
                          key={y}
                          value={y}
                          style={{ background: '#2f3136', color: 'white' }}
                        >
                          {y}
                        </option>
                      ))}
                  </Select>
                </FormControl>
                {/* Toggle per mostrare/nascondere ordini gratuiti */}
                <FormControl
                  display="flex"
                  alignItems="flex-end"
                  width="auto"
                  pb={1}
                >
                  <FormLabel
                    htmlFor="show-free-orders"
                    mb="0"
                    fontSize="sm"
                    color="gray.300"
                    mr={2}
                  >
                    Mostra ordini gratuiti
                  </FormLabel>
                  <Switch
                    id="show-free-orders"
                    isChecked={showFreeOrders}
                    onChange={(e) => setShowFreeOrders(e.target.checked)}
                    colorScheme="yellow"
                    size="md"
                  />
                </FormControl>
              </HStack>
            </Flex>
            <Divider mb={6} borderColor="gray.600" />

            <EventPaymentSummaryPanel
              summaries={eventPaymentSummaries}
              onFocusOrder={handleFocusOrder}
              onPaySaldo={onHandleSaldo}
              onPayPending={(orderId) => {
                const order = filteredOrders.find(
                  (item) =>
                    String(item.documentId || item.id) === String(orderId)
                );
                if (!order) return;
                const index = displayedOrders.findIndex(
                  (item) =>
                    String(item.documentId || item.id) === String(orderId)
                );
                if (index >= 0) {
                  setSelectedIndex(index);
                }
                onHandleMainPagaOra(order);
              }}
            />

            {/* Griglia di card per ciascun ordine */}
            <SimpleGrid spacing={2}>
              {displayedOrders.length === 0 ? (
                <Text color="gray.400" py={4}>
                  Nessun ordine per l&apos;anno selezionato.
                </Text>
              ) : (
                displayedOrders.map((order, index) => {
                  // Determina il colore del badge in base allo stato del pagamento
                  const paymentStatusColor = getPaymentStatusColor(
                    order.paymentStatus
                  );
                  const paymentStatusLabel = getPaymentStatusLabel(
                    order.paymentStatus
                  );
                  const isOrderPaid = ['succeeded', 'processing'].includes(
                    order.paymentStatus
                  );
                  const isFreeOrder = order.event?.is_free || order.price === 0;
                  const merchFulfillment = order.metadata?.merch;
                  const merchStatus = merchFulfillment?.fulfillmentStatus;

                  const ordersDaSaldare = order.metadata?.products?.filter(
                    ({ paymentType }) => paymentType === 'acconto'
                  );

                  // Per mostrare un titolo, usiamo il nome del prodotto/evento se disponibile,
                  // seguito dal nome del socio, o un titolo generico con l'id
                  let displayName = '';

                  // Priorità: prodotto > evento > socio > generico
                  if (
                    order.product &&
                    (order.product.nome || order.product.attributes?.nome)
                  ) {
                    // Ordine prodotto
                    const productName =
                      order.product.nome || order.product.attributes?.nome;
                    displayName = productName;
                    if (order.socio && order.socio.nomeSocio) {
                      displayName += ` - ${order.socio.nomeSocio}`;
                      if (order.socio.cognomeSocio) {
                        displayName += ` ${order.socio.cognomeSocio}`;
                      }
                    }
                  } else if (order.event && order.event.Nome) {
                    // Ordine evento
                    displayName = order.event.Nome;
                    if (order.socio && order.socio.nomeSocio) {
                      displayName += ` - ${order.socio.nomeSocio}`;
                      if (order.socio.cognomeSocio) {
                        displayName += ` ${order.socio.cognomeSocio}`;
                      }
                    }
                  } else if (order.socio && order.socio.nomeSocio) {
                    // Solo socio (fallback)
                    displayName = order.socio.nomeSocio;
                    if (order.socio.cognomeSocio) {
                      displayName += ` ${order.socio.cognomeSocio}`;
                    }
                  } else {
                    // Fallback generico
                    displayName = `Ordine ${order.id}`;
                  }

                  const calculatedDiscountAmount =
                    order.metadata?.products.reduce((accumulatore, product) => {
                      if (
                        product.applyDiscount &&
                        order.event &&
                        product.paymentType !== 'acconto'
                      ) {
                        if (product.paymentType === 'saldo') {
                          const scontoAmount =
                            order.event?.Sconto_Fratelli ?? 0;
                          const prezzoEventoScontato =
                            order.event?.Sconto_Fratelli != null &&
                            order.event?.Sconto_Fratelli !== ''
                              ? order.event.prezzo -
                                (parseFloat(scontoAmount) || 0)
                              : (order.event?.prezzo ?? 0);
                          return (
                            accumulatore +
                            (prezzoEventoScontato - product.amount)
                          );
                        }
                        return (
                          accumulatore + (order.event.prezzo - product.amount)
                        );
                      }
                      return accumulatore;
                    }, 0);

                  return (
                    <span key={order.documentId}>
                      <Box
                        ref={(el) => {
                          const currentOrderId = String(
                            order.documentId || order.id || ''
                          );
                          if (el) {
                            orderRefs.current[currentOrderId] = el;
                          }
                        }}
                        data-order-id={String(
                          order.documentId || order.id || ''
                        )}
                        p={4}
                        borderRadius="12px"
                        boxShadow="sm"
                        transition="all 0.3s ease"
                        bg={selectedIndex === index ? '#2f3136' : '#36393f'}
                        border="1px solid"
                        borderColor={
                          selectedIndex === index ? '#ffcc00' : 'gray.600'
                        }
                        _hover={{
                          transform:
                            selectedIndex === index ? '' : 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                        }}
                        _active={{ transform: 'scale(1)' }}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Box>
                            <Text
                              fontWeight="bold"
                              color="white"
                              fontSize={{ base: 'sm', md: 'md' }}
                              data-clarity-mask="True"
                            >
                              {displayName}
                            </Text>
                            <Text
                              color="gray.400"
                              mt={1}
                              fontSize={{ base: 'xs', md: 'sm' }}
                            >
                              {formatDateAbbr(order.createdAt, {
                                withTime: true,
                              })}
                            </Text>
                          </Box>

                          <Flex
                            justify="space-between"
                            align={{ base: 'flex-start', md: 'center' }}
                            wrap="wrap"
                            gap={3}
                          >
                            <HStack spacing={2} flexWrap="wrap">
                              {isFreeOrder ? (
                                <Badge
                                  bg="#3eb297"
                                  color="white"
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  Gratuito
                                </Badge>
                              ) : (
                                <Badge
                                  bg="gray.700"
                                  color="white"
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  {calculatedDiscountAmount ? (
                                    <>
                                      <Text
                                        as="span"
                                        color="gray.300"
                                        textDecor="line-through"
                                        mr={1}
                                      >
                                        €
                                        {(
                                          order.price + calculatedDiscountAmount
                                        )?.toFixed(2)}
                                      </Text>
                                      <Text as="span">
                                        €{order.price?.toFixed(2)}
                                      </Text>
                                    </>
                                  ) : (
                                    <>€{order.price?.toFixed(2)}</>
                                  )}
                                </Badge>
                              )}

                              <Badge
                                colorScheme={paymentStatusColor}
                                borderRadius="10px"
                                px={3}
                                py={1}
                                fontSize="xs"
                                fontWeight="600"
                              >
                                {paymentStatusLabel}
                              </Badge>

                              {merchStatus && (
                                <Badge
                                  colorScheme={getMerchFulfillmentColor(
                                    merchStatus
                                  )}
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  {merchFulfillment?.isPreorder &&
                                  merchStatus === 'awaiting_stock'
                                    ? 'Prenotazione'
                                    : getMerchFulfillmentLabel(merchStatus, {
                                        short: true,
                                      })}
                                </Badge>
                              )}

                              {!isOrderPaid && (
                                <Badge
                                  colorScheme="red"
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  Da saldare
                                </Badge>
                              )}

                              {Boolean(ordersDaSaldare.length) && (
                                <Badge
                                  bg="#ffcc00"
                                  color="black"
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  Acconto
                                </Badge>
                              )}

                              {orderHasSiblingDiscount(order) && (
                                <SiblingDiscountBadge />
                              )}

                              {orderHasAdminOverrideBug(order) && (
                                <Badge
                                  colorScheme="orange"
                                  borderRadius="10px"
                                  px={3}
                                  py={1}
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  Verifica pagamento
                                </Badge>
                              )}
                            </HStack>

                            <Button
                              size="sm"
                              colorScheme={
                                selectedIndex === index ? 'red' : 'blue'
                              }
                              onClick={() => handleCardClick(index)}
                              minW={{ base: 'auto', md: '140px' }}
                            >
                              {selectedIndex === index
                                ? 'Chiudi dettagli'
                                : 'Mostra dettagli'}
                            </Button>
                          </Flex>
                        </VStack>
                      </Box>
                      {/* Se è stata selezionata una card, mostra i dettagli dell'ordine */}
                      {selectedIndex === index && (
                        <Box mb={3}>
                          <DettaglioOrdine
                            orders={displayedOrders}
                            order={displayedOrders[selectedIndex]}
                            onPaymentSuccess={onPaymentSuccess}
                            onHandleSaldo={onHandleSaldo}
                            onHandleMainPagaOra={onHandleMainPagaOra}
                          />
                        </Box>
                      )}
                    </span>
                  );
                })
              )}
            </SimpleGrid>
            {selectedOrderToPay && (
              <Drawer
                size="sm"
                isOpen={Boolean(selectedOrderToPay)}
                placement="right"
                onClose={onModalClose}
              >
                <DrawerOverlay />
                <DrawerContent bg="#2f3136" color="white">
                  <DrawerCloseButton color="white" />
                  <DrawerBody>
                    <Box p="12px 0" w={400}>
                      <Text fontSize="lg" mb={2} color="white">
                        Paga ora per saldare l&apos;ordine in sospeso:
                        <br />
                        <b>{selectedOrderToPay.name}</b>
                      </Text>
                      <Divider mb={2} borderColor="gray.600" />
                      {paymentSuccess ? (
                        <Alert
                          status="success"
                          borderRadius="md"
                          bg="green.600"
                          color="white"
                        >
                          <AlertIcon />
                          Pagamento avvenuto con successo 🚀
                        </Alert>
                      ) : (
                        <>
                          <Text
                            fontSize="2xl"
                            mb={2}
                            mt={6}
                            color="brand.300"
                            textAlign="left"
                            width="100%"
                          >
                            Totale:{' '}
                            <b>
                              €
                              {(() => {
                                // Calcola l'importo totale basandosi sui prezzi effettivi delle varianti
                                const totalAmount =
                                  selectedOrderToPay.metadata.products.reduce(
                                    (sum, product) => {
                                      const productPrice =
                                        product.type === 'variant' &&
                                        product.prezzoVariante !== undefined
                                          ? product.prezzoVariante
                                          : product.amount;
                                      return sum + productPrice;
                                    },
                                    0
                                  );
                                return totalAmount.toFixed(2);
                              })()}
                            </b>
                          </Text>
                          <Divider />
                          {!loadingSecret && clientSecret ? (
                            <Elements
                              stripe={stripePromise}
                              options={{
                                clientSecret,
                              }}
                            >
                              <PaymentForm
                                ambito="ordine"
                                onSuccess={({
                                  status,
                                  paymentIntentId,
                                  paymentMethod,
                                  paymentMethodId,
                                  paymentStatus,
                                }) => {
                                  if (
                                    ['succeeded', 'processing'].includes(status)
                                  ) {
                                    updateOrder({
                                      orderId: selectedOrderToPay.documentId,
                                      paymentMethodId,
                                      paymentMethod,
                                      paymentIntentId,
                                      paymentStatus,
                                    });
                                  }
                                }}
                              />
                            </Elements>
                          ) : (
                            <Center width="100%" minHeight="200px">
                              <Spinner size="md" mt={2} />
                            </Center>
                          )}
                          {Boolean(paymentError) && (
                            <Alert status="error" mb={4}>
                              <AlertIcon />
                              {paymentError}
                            </Alert>
                          )}
                        </>
                      )}
                      <Divider mt={4} />
                      <Button
                        width="100%"
                        mt={4}
                        onClick={() => setSelectedProductToPay('')}
                      >
                        Chiudi
                      </Button>
                    </Box>
                  </DrawerBody>
                </DrawerContent>
              </Drawer>
            )}
            {selectedProductDataToPay && (
              <Drawer
                size="sm"
                isOpen={Boolean(selectedProductDataToPay)}
                placement="right"
                onClose={onModalClose}
              >
                <DrawerOverlay />
                <DrawerContent bg="#2f3136" color="white">
                  <DrawerCloseButton color="white" />
                  <DrawerBody>
                    <Box p="12px 0" w={400}>
                      <Text fontSize="lg" mb={2} color="white">
                        Paga il saldo per:
                        <br />
                        <b>{selectedProductDataToPay.name}</b>
                      </Text>
                      <Divider mb={2} borderColor="gray.600" />
                      {paymentSuccess ? (
                        <Alert
                          status="success"
                          borderRadius="md"
                          bg="green.600"
                          color="white"
                        >
                          <AlertIcon />
                          Pagamento avvenuto con successo 🚀
                        </Alert>
                      ) : (
                        <>
                          <Text
                            fontSize="2xl"
                            mb={2}
                            mt={6}
                            color="brand.300"
                            textAlign="left"
                            width="100%"
                          >
                            Totale:{' '}
                            <b>€{selectedProductDataToPay.amount.toFixed(2)}</b>
                          </Text>
                          <Divider />
                          {!loadingSecret && clientSecret ? (
                            <Elements
                              stripe={stripePromise}
                              options={{
                                clientSecret,
                              }}
                            >
                              <PaymentForm
                                ambito="ordine"
                                onSuccess={({
                                  status,
                                  paymentIntentId,
                                  paymentMethod,
                                  paymentMethodId,
                                  paymentStatus,
                                }) => {
                                  if (
                                    ['succeeded', 'processing'].includes(status)
                                  ) {
                                    createOrder({
                                      paymentMethodId,
                                      paymentMethod,
                                      paymentIntentId,
                                      paymentStatus,
                                    });
                                  }
                                }}
                              />
                            </Elements>
                          ) : (
                            <Center width="100%" minHeight="200px">
                              <Spinner size="md" mt={2} />
                            </Center>
                          )}
                          {Boolean(paymentError) && (
                            <Alert status="error" mb={4}>
                              <AlertIcon />
                              {paymentError}
                            </Alert>
                          )}
                        </>
                      )}
                      <Divider mt={4} />
                      <Button
                        width="100%"
                        mt={4}
                        onClick={() => setSelectedProductToPay('')}
                      >
                        Chiudi
                      </Button>
                    </Box>
                  </DrawerBody>
                </DrawerContent>
              </Drawer>
            )}
          </>
        ) : (
          <>
            <Heading size="lg" mb={4} color="white" id="ordini-title">
              I Tuoi Ordini
            </Heading>
            <Divider mb={6} borderColor="gray.600" />
            <Text color="white">Al momento non hai nessun ordine.</Text>
          </>
        )}
      </Box>
      <Box
        as="span"
        bg="#36393f"
        p={6}
        display="block"
        borderRadius="12px"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.600"
        mt={6}
      >
        <Heading size="lg" mb={4} color="white" fontWeight="700">
          Come pagare un&apos;attività
        </Heading>
        <Divider mb={6} borderColor="gray.600" />
        <Text color="gray.300" mb={4}>
          Per pagare un&apos;attività, seleziona l&apos;ordine corrispondente
          dalla lista sopra e clicca su &quot;Mostra Dettagli&quot;. Segui le
          istruzioni per completare il pagamento online. Puoi pagare con carta
          di credito o altri metodi di pagamento supportati.
        </Text>
        <Text color="gray.300" mb={4}>
          Se hai domande o hai bisogno di assistenza, non esitare a contattarci.
        </Text>
        <PaymentMethods />
      </Box>
    </>
  );
};

export default Orders;
